// checkNetworkSpeed.js
'use strict';
import getOperationData from '../selector/getOperationData.js';
import getMeData from '../selector/getMeData.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';
import { MERGE_OPERATION_DATA, REMOVE_OPERATION_DATA } from '../ActionTypes.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import loadSpeedtest from '../resource/loadSpeedtest.js';
import pushToastr from '../action/pushToastr.js';

let speedtest = undefined;

/**
 * Check network speed
 * @kind action
 * @param {string} speedtestServersUrl - Speedtest Servers url
 * @return {Promise} Action promise.
 */
const checkNetworkSpeed =
  ({ speedtestServersUrl }) =>
  async dispatch => {
    if (!speedtest) {
      await loadSpeedtest({
        speedtestSrc: '/static/js/speedtest.js',
      });

      speedtest = new window.Speedtest();

      speedtest.onupdate = data => {
        if (!data) return;

        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['speedtest'],
            data: {
              clientIp: data.clientIp,
              dlProgress: data.dlProgress,
              ulProgress: data.ulProgress,
              pingStatus: data.pingStatus,
              jitterStatus: data.jitterStatus,
              dlStatus: data.dlStatus,
              ulStatus: data.ulStatus,
              testState: data.testState,
            },
          },
        });
      };

      speedtest.onend = aborted => {
        if (aborted) {
          // reset
          dispatch({
            type: REMOVE_OPERATION_DATA,
            payload: {
              selectPath: ['speedtest'],
            },
          });
        }
        return dispatch(setSpeedtestUnixTimestamp());
      };

      speedtest.setParameter('mpot', true);

      speedtest.loadServerList(speedtestServersUrl, result => {
        if (!result) {
          dispatch(
            pushToastr({
              textKey: 'No Speedtest Server List',
              durationSec: 5,
              color: 'error',
            })
          );
        }

        speedtest.selectServer(server => {
          if (server) speedtest.start();
        });
      });
    }

    return dispatch({ type: '' });
  };

const setSpeedtestUnixTimestamp = () => async (dispatch, getState) => {
  const timestampOffsetSeconds = getTimestampOffset(
    getState(),
    Accuracy.SECOND
  );
  return dispatch({
    type: MERGE_OPERATION_DATA,
    payload: {
      selectPath: ['speedtest'],
      data: {
        speedtestUnixTimestamp: getCurrentUnixTimestamp({
          offsetSeconds: timestampOffsetSeconds,
        }),
      },
    },
  });
};

export const trackSpeedtestEvent = () => async (_, getState) => {
  const drmTestResult = getOperationData(getState(), ['drm'], 'testResult');
  const drmTestMessageId = getOperationData(getState(), ['drm'], 'messageId');
  const initSpeed = getOperationData(
    getState(),
    ['pusher', 'stats'],
    'initSpeed'
  );
  const connectSpeed = getOperationData(
    getState(),
    ['pusher', 'stats'],
    'connectSpeed'
  );
  const jitterStatus = getOperationData(
    getState(),
    ['speedtest'],
    'jitterStatus'
  );
  const pingStatus = getOperationData(getState(), ['speedtest'], 'pingStatus');
  const dlProgress = getOperationData(getState(), ['speedtest'], 'dlProgress');
  const ulProgress = getOperationData(getState(), ['speedtest'], 'ulProgress');
  const dlStatus = getOperationData(getState(), ['speedtest'], 'dlStatus');
  const ulStatus = getOperationData(getState(), ['speedtest'], 'ulStatus');
  const testState = getOperationData(getState(), ['speedtest'], 'testState');
  const jwtData = getMeData(getState(), 'jwtData');
  const refreshTokenJwtData = getMeData(getState(), 'refreshTokenJwtData');
  const fcmToken = getMeData(getState(), 'fcmToken');

  return trackMixpanel({
    type: EventTypes.USER_DEBUG_SENT,
    payload: {
      drmTestResult,
      drmTestMessageId,
      speedtestResult: {
        jitterStatus,
        pingStatus,
        dlProgress,
        ulProgress,
        dlStatus,
        ulStatus,
        testState,
      },
      pusherStatsResult: {
        initSpeed,
        connectSpeed,
      },
      tokens: {
        accessToken: jwtData,
        refreshToken: refreshTokenJwtData,
      },
      fcmToken,
    },
  });
};

export default checkNetworkSpeed;
